import { BlurredBackgroundImage } from '@/components/ui/blurred-background-image';
import { GlobalFullBleed } from '@/components/ui/global-full-bleed';
import { PackHeroVariant } from '@/utils/constants';
import { routes } from '@/utils/routes';
import {
    // eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
    DS_COLOR,
} from '@calm/evexia-design-tokens';
import { MediaImage } from '@calm/evexia-web-sdk';
import BackButton from '@rhg/components/back-button';
import { Heading, SubtitlePosition } from '@rhg/components/heading';
import { HeroBannerImage } from '@rhg/components/ui/hero-banner-image';
import { BANNER_HEIGHT } from '@rhg/components/ui/hero-banner-image/types';
import { HeroHeaderWrapper } from '@rhg/components/ui/hero-header-wrapper';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { Box, Theme, useTheme } from '@rhg/libs/material';
import { MaskOrientation, styleMaskedContainer } from '@rhg/libs/material/sx';
import { ComponentProps } from 'react';

const styleNoImagePackDetailsHeader = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    py: (theme: Theme) => theme.ds.spacingUnits.SECTION,
};

const stylePackDetailsHeader = {
    py: (theme: Theme) => theme.ds.spacingUnits.SECTION,
};

const styleCenteredHeroBackground = {
    position: 'relative',
    height: BANNER_HEIGHT,
};

// Absolutely fills styleCenteredHeroBackground — only rendered on desktop,
// as a sibling of styleCenteredHeroContent rather than its wrapper, so the
// content can be positioned the same way whether or not it's present. The
// black backdrop (matching the Figma "Blur Background Image" reference) is
// what the huge blur below fades to at the frame's edges — there's no
// separate tint/overlay layer in the design, just this solid backdrop.
const styleCenteredHeroBlurredBackground = {
    position: 'absolute',
    inset: 0,
    backgroundColor: DS_COLOR.BLACK_100P,
};

const styleCenteredHeroContent = {
    position: 'relative',
    zIndex: 1,
    height: '100%',
};

const styleCenteredHeroBackButton = {
    position: 'absolute',
    top: (theme: Theme) => ({
        xs: theme.semantics.Spacing[16],
        md: theme.semantics.Spacing[24],
        lg: theme.semantics.Spacing[32],
    }),
    left: (theme: Theme) => ({
        xs: theme.semantics.Spacing[16],
        md: theme.semantics.Spacing[24],
        lg: theme.semantics.Spacing[32],
    }),
};

const styleCenteredHeroImageContainer = { justifyContent: 'center' };

// How much of the image's own width, on each side, fades to transparent —
// blends its hard edge into the blurred backdrop behind it (matching the
// Figma "Center Masked" reference) rather than a sharp rectangular cutoff.
const CENTERED_HERO_EDGE_FADE_PERCENT = 40;

// Height-constrained instead of the default width-stretched cover, so the
// image keeps its true 3:2 shape rather than filling the full banner width.
const styleCenteredHeroImage = {
    width: 'auto',
    ...styleMaskedContainer(
        MaskOrientation.Right,
        1 - CENTERED_HERO_EDGE_FADE_PERCENT / 100,
        0,
        true
    ),
};

// Matches the Figma "Blur Background Image" reference (Layer blur: 200) —
// much stronger than BlurredBackgroundImage's 50px default, so the image
// dissolves into the black backdrop above rather than staying sharp.
const CENTERED_HERO_BLUR_PX = 200;

export const PackDetailsHeader = ({
    title,
    subtitle,
    imageData,
    reversed,
    subtitleAlign = 'center',
    spacing,
    heroVariant,
}: {
    title?: string;
    subtitle?: string;
    reversed?: boolean;
    subtitleAlign?: ComponentProps<typeof Heading>['subtitleAlign'];
    spacing?: ComponentProps<typeof Heading>['spacing'];
    imageData?: MediaImage;
    heroVariant?: PackHeroVariant;
}) => {
    const theme = useTheme();
    const isMobile = useIsMobile();

    if (!imageData) {
        // NOTE - once all pages that use this back button are updated with the new Global Grid Context, we can remove the sx prop from the BackButton
        return (
            <Box sx={styleNoImagePackDetailsHeader}>
                <Heading
                    leftButton={
                        <BackButton
                            fallbackRoute={routes.discover}
                            sx={{ left: 0 }}
                        />
                    }
                    title={title ?? ''}
                    titleAlign="center"
                    subtitle={subtitle}
                    subtitlePosition={SubtitlePosition.Inline}
                    reversed={reversed}
                    subtitleAlign={subtitleAlign}
                    subtitleWillRenderLinks
                    subTitleColumns={{ xs: 12, sm: 12, md: 12, lg: 10, xl: 8 }}
                    spacing={
                        spacing ??
                        (reversed
                            ? theme.ds.spacingUnits.BASE
                            : theme.ds.spacingUnits.SECTION)
                    }
                />
            </Box>
        );
    }

    return (
        <>
            <GlobalFullBleed>
                {heroVariant === PackHeroVariant.Centered ? (
                    <Box
                        data-testid="centered-hero"
                        sx={styleCenteredHeroBackground}
                    >
                        {!isMobile && (
                            <BlurredBackgroundImage
                                orientation={MaskOrientation.Top}
                                src={imageData?.url}
                                sx={styleCenteredHeroBlurredBackground}
                                blurPx={CENTERED_HERO_BLUR_PX}
                                hideOverlay
                            />
                        )}
                        <Box sx={styleCenteredHeroContent}>
                            <BackButton
                                fallbackRoute={routes.discover}
                                sx={styleCenteredHeroBackButton}
                            />
                            <HeroBannerImage
                                heroImageSrc={imageData?.url}
                                sxBannerImageContainer={
                                    isMobile
                                        ? undefined
                                        : styleCenteredHeroImageContainer
                                }
                                sxBannerImage={
                                    isMobile
                                        ? undefined
                                        : styleCenteredHeroImage
                                }
                            />
                        </Box>
                    </Box>
                ) : (
                    <HeroHeaderWrapper
                        hideImageMask
                        heroImageSrc={imageData?.url}
                        fallbackBackRoute={routes.discover}
                    />
                )}
            </GlobalFullBleed>
            <Box sx={stylePackDetailsHeader}>
                <Heading
                    title={title}
                    titleAlign="left"
                    subtitle={subtitle}
                    subtitleAlign={subtitleAlign}
                    subtitleWillRenderLinks
                    spacing={
                        spacing ??
                        (reversed
                            ? theme.ds.spacingUnits.BASE
                            : theme.ds.spacingUnits.SECTION)
                    }
                />
            </Box>
        </>
    );
};
